import { z } from 'zod';
import { dayKeySchema } from './contacts.js';
/**
 * Vocabulary and step schemas of the self-disclosure form (Selbstauskunft 03–10, PLAN.md §3.9) shared
 * by the API (validation of the public form, labels of the internal views) and the SPA (the form under
 * `/sd/<token>`, the pills and rows of the record). German strings are the verbatim texts of the
 * screens; identifiers follow docs/glossary.md.
 */
// --- steps -----------------------------------------------------------------------------------------
export const selfDisclosureSteps = ['person', 'income', 'expenses', 'assets', 'signature'];
/** The four steps the customer saves through `PATCH …/steps/:step`; the fifth is the submission. */
export const selfDisclosureFormSteps = ['person', 'income', 'expenses', 'assets'];
/** Sidebar stepper of the form (Selbstauskunft 03). */
export const selfDisclosureStepLabels = {
    person: 'Person',
    income: 'Einkommen',
    expenses: 'Ausgaben',
    assets: 'Vermögen',
    signature: 'Unterschrift',
};
export const selfDisclosureStepNumbers = {
    person: 1,
    income: 2,
    expenses: 3,
    assets: 4,
    signature: 5,
};
// --- enums and labels ------------------------------------------------------------------------------
export const selfDisclosureEmployments = ['employed', 'civil_servant', 'self_employed', 'retired'];
/** Segmented control „Beschäftigung" (Selbstauskunft 03). */
export const selfDisclosureEmploymentLabels = {
    employed: 'Angestellt',
    civil_servant: 'Beamtet',
    self_employed: 'Selbstständig',
    retired: 'Rente',
};
export const selfDisclosureContracts = ['permanent', 'fixed_term'];
/** Radio „Arbeitsvertrag" (Selbstauskunft 03). */
export const selfDisclosureContractLabels = {
    permanent: 'Unbefristet',
    fixed_term: 'Befristet',
};
export const selfDisclosureHousingSituations = ['rent', 'ownership', 'parents'];
/** Segmented control „Aktuelle Wohnsituation" (Selbstauskunft 04). */
export const selfDisclosureHousingSituationLabels = {
    rent: 'Zur Miete',
    ownership: 'Eigentum',
    parents: 'Bei Eltern',
};
export const selfDisclosureMaritalStatuses = [
    'single',
    'married',
    'partnership',
    'divorced',
    'widowed',
];
/** „Familienstand" as the summary shows it („ledig"). */
export const selfDisclosureMaritalStatusLabels = {
    single: 'ledig',
    married: 'verheiratet',
    partnership: 'eingetragene Lebenspartnerschaft',
    divorced: 'geschieden',
    widowed: 'verwitwet',
};
export const selfDisclosureOtherIncomeTypes = ['side_job', 'child_benefit', 'rental', 'other'];
/** Dropdown „Art der weiteren Einnahmen" (Selbstauskunft 03: „z. B. Nebentätigkeit, Kindergeld, bestehende Mieteinnahmen"). */
export const selfDisclosureOtherIncomeTypeLabels = {
    side_job: 'Nebentätigkeit',
    child_benefit: 'Kindergeld',
    rental: 'Bestehende Mieteinnahmen',
    other: 'Sonstige',
};
export const selfDisclosureSalariesPerYear = [12, 13, 14];
export const selfDisclosureProofTypes = ['id_document', 'salary_proof', 'equity', 'other'];
/** Upload cards of the proofs (Portal 06: „Personalausweis", „Gehaltsnachweise, 3 Monate", „Eigenkapitalnachweis"). */
export const selfDisclosureProofTypeLabels = {
    id_document: 'Personalausweis',
    salary_proof: 'Gehaltsnachweise, 3 Monate',
    equity: 'Eigenkapitalnachweis',
    other: 'Weiterer Nachweis',
};
export const selfDisclosureProofStatuses = ['uploaded', 'reviewed'];
/** Portal 29: „ungeprüft" / „geprüft". */
export const selfDisclosureProofStatusLabels = {
    uploaded: 'ungeprüft',
    reviewed: 'geprüft',
};
/** Limits of the public form (PLAN.md §7/§9.4, Portal 16). */
export const selfDisclosureFormRules = {
    /** Proofs: PDF/JPG/PNG up to 10 MB. */
    uploadMaxMb: 10,
    /** The signature PNG of the canvas. */
    signatureMaxKb: 512,
    /** „Finanzierungsvolumen gültig bis" — 90 days after the calculation. */
    volumeValidityDays: 90,
    /** Requests per minute per link and address before the API answers 429. */
    rateLimitPerMinute: 60,
};
// --- field catalog (Selbstauskunft 08 groups, 09 comparison rows) ---------------------------------
export const selfDisclosureFieldGroups = ['person', 'income', 'expenses', 'assets'];
/**
 * Every visible row of the internal views in screen order. Rows that the screens show as one line
 * (Arbeitsvertrag „befristet bis 12/2026", Warmmiete „1.240 € · bleibt", Leasing „289 € · bis 03/2028")
 * combine their columns.
 */
export const selfDisclosureFieldCatalog = [
    { group: 'person', field: 'name', label: 'Name', columns: ['firstName', 'lastName'] },
    { group: 'person', field: 'bornOn', label: 'Geburtsdatum', columns: ['bornOn'] },
    { group: 'person', field: 'maritalStatus', label: 'Familienstand', columns: ['maritalStatus'] },
    { group: 'person', field: 'children', label: 'Kinder', columns: ['children'] },
    { group: 'person', field: 'nationality', label: 'Staatsangehörigkeit', columns: ['nationality'] },
    { group: 'person', field: 'address', label: 'Anschrift', columns: ['street', 'postalCode', 'city'] },
    { group: 'person', field: 'phone', label: 'Telefon', columns: ['phone'] },
    { group: 'person', field: 'email', label: 'E-Mail', columns: ['email'] },
    { group: 'income', field: 'employment', label: 'Beschäftigung', columns: ['employment'] },
    { group: 'income', field: 'employer', label: 'Arbeitgeber', columns: ['employer'] },
    { group: 'income', field: 'employedSinceOn', label: 'Beschäftigt seit', columns: ['employedSinceOn'] },
    { group: 'income', field: 'contract', label: 'Arbeitsvertrag', columns: ['contract', 'fixedTermUntilOn'] },
    { group: 'income', field: 'netIncomeCents', label: 'Nettoeinkommen', columns: ['netIncomeCents'] },
    { group: 'income', field: 'salariesPerYear', label: 'Gehälter pro Jahr', columns: ['salariesPerYear'] },
    {
        group: 'income',
        field: 'specialPaymentsPaCents',
        label: 'Sonderzahlungen pro Jahr',
        columns: ['specialPaymentsPaCents'],
    },
    {
        group: 'income',
        field: 'otherIncomeCents',
        label: 'Weitere Einnahmen',
        columns: ['otherIncomeCents', 'otherIncomeType'],
    },
    {
        group: 'income',
        field: 'rentalIncomeCents',
        label: 'Bestehende Mieteinnahmen',
        columns: ['rentalIncomeCents'],
    },
    {
        group: 'expenses',
        field: 'housingSituation',
        label: 'Aktuelle Wohnsituation',
        columns: ['housingSituation'],
    },
    {
        group: 'expenses',
        field: 'grossRentCents',
        label: 'Warmmiete',
        columns: ['grossRentCents', 'rentContinues'],
    },
    {
        group: 'expenses',
        field: 'loansCents',
        label: 'Leasing und Ratenkredite',
        columns: ['loansCents', 'loansRemainingTerm'],
    },
    { group: 'expenses', field: 'alimonyCents', label: 'Unterhaltszahlungen', columns: ['alimonyCents'] },
    {
        group: 'expenses',
        field: 'privateHealthInsuranceCents',
        label: 'Private Krankenversicherung',
        columns: ['privateHealthInsuranceCents'],
    },
    { group: 'expenses', field: 'leasingCents', label: 'Leasing', columns: ['leasingCents'] },
    { group: 'assets', field: 'equityCents', label: 'Eigenkapital', columns: ['equityCents'] },
    { group: 'assets', field: 'securitiesCents', label: 'Wertpapiere', columns: ['securitiesCents'] },
    { group: 'assets', field: 'otherCents', label: 'Sonstiges Vermögen', columns: ['otherCents'] },
];
// --- step schemas (the public form's PATCH bodies; the SPA validates with the same rules) ----------
export const AMOUNT_MESSAGE = 'Bitte geben Sie einen Betrag in Euro ein.';
/** Selbstauskunft 04 field error of „Private Krankenversicherung". */
export const HEALTH_INSURANCE_MESSAGE = 'Bitte nur Ziffern und den Monatsbeitrag eintragen, z. B. 412.';
/** Selbstauskunft 04 field error of „Warmmiete". */
export const GROSS_RENT_MESSAGE = 'Bitte geben Sie Ihre aktuelle Warmmiete an.';
export const NET_INCOME_MESSAGE = 'Bitte geben Sie Ihr Nettoeinkommen an.';
export const MONTH_MESSAGE = 'Bitte einen Monat im Format MM/JJJJ angeben.';
/** `YYYY-MM` — the form asks for „MM/JJJJ" (Beschäftigt seit, befristet bis, Restlaufzeit). */
export const monthKeySchema = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, MONTH_MESSAGE);
/** A monthly amount in cents: a whole, non-negative number. */
export function amountCentsSchema(message = AMOUNT_MESSAGE) {
    return z.number({ error: message }).int({ error: message }).min(0, { error: message });
}
const optionalText = (max) => z.string().trim().max(max).nullable().optional();
export const selfDisclosurePersonStepSchema = z.object({
    firstName: z
        .string({ error: 'Bitte geben Sie Ihren Vornamen an.' })
        .trim()
        .min(1, { error: 'Bitte geben Sie Ihren Vornamen an.' })
        .max(100),
    lastName: z
        .string({ error: 'Bitte geben Sie Ihren Nachnamen an.' })
        .trim()
        .min(1, { error: 'Bitte geben Sie Ihren Nachnamen an.' })
        .max(100),
    bornOn: dayKeySchema.nullable().optional(),
    maritalStatus: z
        .enum(selfDisclosureMaritalStatuses, { error: 'Bitte wählen Sie Ihren Familienstand.' })
        .nullable()
        .optional(),
    children: z
        .number({ error: 'Bitte geben Sie die Anzahl Ihrer Kinder an.' })
        .int({ error: 'Bitte geben Sie die Anzahl Ihrer Kinder an.' })
        .min(0, { error: 'Bitte geben Sie die Anzahl Ihrer Kinder an.' })
        .max(20, { error: 'Bitte geben Sie die Anzahl Ihrer Kinder an.' })
        .nullable()
        .optional(),
    nationality: optionalText(100),
    street: optionalText(200),
    postalCode: optionalText(12),
    city: optionalText(120),
    phone: optionalText(40),
    email: z
        .string()
        .trim()
        .email({ error: 'Bitte geben Sie eine gültige E-Mail-Adresse an.' })
        .max(200)
        .nullable()
        .optional(),
});
export const selfDisclosureIncomeStepSchema = z
    .object({
    employment: z.enum(selfDisclosureEmployments, { error: 'Bitte wählen Sie Ihre Beschäftigung.' }),
    employer: optionalText(200),
    employedSinceOn: monthKeySchema.nullable().optional(),
    contract: z
        .enum(selfDisclosureContracts, { error: 'Bitte wählen Sie Ihren Arbeitsvertrag.' })
        .nullable()
        .optional(),
    fixedTermUntilOn: monthKeySchema.nullable().optional(),
    netIncomeCents: amountCentsSchema(NET_INCOME_MESSAGE).min(1, { error: NET_INCOME_MESSAGE }),
    salariesPerYear: z
        .union([z.literal(12), z.literal(13), z.literal(14)], {
        error: 'Bitte wählen Sie 12, 13 oder 14 Gehälter pro Jahr.',
    })
        .nullable()
        .optional(),
    specialPaymentsPaCents: amountCentsSchema().nullable().optional(),
    otherIncomeCents: amountCentsSchema().nullable().optional(),
    otherIncomeType: z
        .enum(selfDisclosureOtherIncomeTypes, { error: 'Bitte wählen Sie die Art der weiteren Einnahmen.' })
        .nullable()
        .optional(),
    rentalIncomeCents: amountCentsSchema().nullable().optional(),
})
    .superRefine((value, ctx) => {
    const employed = value.employment === 'employed' || value.employment === 'civil_servant';
    if (employed && !value.employer) {
        ctx.addIssue({ code: 'custom', path: ['employer'], message: 'Bitte geben Sie Ihren Arbeitgeber an.' });
    }
    if (employed && !value.contract) {
        ctx.addIssue({ code: 'custom', path: ['contract'], message: 'Bitte wählen Sie Ihren Arbeitsvertrag.' });
    }
    if (value.employment !== 'retired' && !value.employedSinceOn) {
        ctx.addIssue({
            code: 'custom',
            path: ['employedSinceOn'],
            message: 'Bitte geben Sie an, seit wann Sie beschäftigt sind.',
        });
    }
    if (value.contract === 'fixed_term' && !value.fixedTermUntilOn) {
        ctx.addIssue({
            code: 'custom',
            path: ['fixedTermUntilOn'],
            message: 'Bitte geben Sie an, bis wann Ihr Vertrag befristet ist.',
        });
    }
});
const expensesFields = {
    /** „Bleibt die Miete nach dem Kauf?" — true = „Ja, ich ziehe nicht ein". */
    rentContinues: z.boolean().optional(),
    loansCents: amountCentsSchema().nullable().optional(),
    loansRemainingTerm: monthKeySchema.nullable().optional(),
    alimonyCents: amountCentsSchema().nullable().optional(),
    privateHealthInsuranceCents: amountCentsSchema(HEALTH_INSURANCE_MESSAGE).nullable().optional(),
    leasingCents: amountCentsSchema().nullable().optional(),
};
/**
 * Step 3 by housing situation (a discriminated union so that every field error of Selbstauskunft 04 is
 * reported at once): „Zur Miete" needs the current gross rent, the other situations do not.
 */
export const selfDisclosureExpensesStepSchema = z.discriminatedUnion('housingSituation', [
    z.object({
        housingSituation: z.literal('rent'),
        grossRentCents: amountCentsSchema(GROSS_RENT_MESSAGE).min(1, { error: GROSS_RENT_MESSAGE }),
        ...expensesFields,
    }),
    z.object({
        housingSituation: z.literal('ownership'),
        grossRentCents: amountCentsSchema().nullable().optional(),
        ...expensesFields,
    }),
    z.object({
        housingSituation: z.literal('parents'),
        grossRentCents: amountCentsSchema().nullable().optional(),
        ...expensesFields,
    }),
], { error: 'Bitte wählen Sie Ihre aktuelle Wohnsituation.' });
export const selfDisclosureAssetsStepSchema = z.object({
    equityCents: amountCentsSchema().nullable().optional(),
    securitiesCents: amountCentsSchema().nullable().optional(),
    otherCents: amountCentsSchema().nullable().optional(),
});
/** Step 5 (Selbstauskunft 05): the two mandatory consents, the optional opt-in, the signature and the place. */
export const selfDisclosureSubmitBodySchema = z.object({
    consentCompleteness: z.literal(true, {
        error: 'Bitte bestätigen Sie, dass Ihre Angaben vollständig und richtig sind.',
    }),
    consentSharing: z.literal(true, { error: 'Bitte willigen Sie in die Weitergabe Ihrer Angaben ein.' }),
    propertyInfoConsent: z.boolean().optional(),
    /** PNG of the signature canvas, base64 (a `data:image/png;base64,` prefix is accepted). */
    signaturePng: z
        .string({ error: 'Bitte unterschreiben Sie.' })
        .min(1, { error: 'Bitte unterschreiben Sie.' })
        .max(selfDisclosureFormRules.signatureMaxKb * 1024 * 2),
    place: z
        .string({ error: 'Bitte geben Sie den Ort an.' })
        .trim()
        .min(1, { error: 'Bitte geben Sie den Ort an.' })
        .max(120),
});
//# sourceMappingURL=self-disclosure.js.map